import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import BotSweepLogo from "@/components/landing/BotSweepLogo";

interface Particle {
  id: number;
  isBot: boolean;
}

const ParticleFlow = ({ inView }: { inView: boolean }) => {
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    if (!inView) return;
    let id = 0;
    const interval = setInterval(() => {
      setParticles((prev) => [
        ...prev.slice(-10),
        { id: id++, isBot: Math.random() > 0.4 },
      ]);
    }, 600);
    return () => clearInterval(interval);
  }, [inView]);

  return (
    <div className="relative w-full h-48 md:h-64 overflow-hidden rounded-xl border border-border/50 bg-background/50">
      {/* Animated BotSweep Logo in center */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
          style={{ filter: "drop-shadow(0 0 8px hsl(var(--primary) / 0.4))" }}
        >
          {/* Pulsing cyan glow ring — peaks at 12 o'clock (0%/100%) synced with sweep arm */}
          <motion.div
            className="absolute inset-[-6px] rounded-full border border-primary/30"
            animate={{
              boxShadow: [
                "0 0 12px hsl(var(--primary) / 0.5), 0 0 28px hsl(var(--primary) / 0.2)",
                "0 0 4px hsl(var(--primary) / 0.08), 0 0 10px hsl(var(--primary) / 0.03)",
                "0 0 12px hsl(var(--primary) / 0.5), 0 0 28px hsl(var(--primary) / 0.2)",
              ],
              borderColor: [
                "hsl(var(--primary) / 0.7)",
                "hsl(var(--primary) / 0.15)",
                "hsl(var(--primary) / 0.7)",
              ],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear", times: [0, 0.5, 1] }}
          />
          {/* Expanding ripple burst on each sweep completion */}
          <motion.div
            className="absolute inset-0 m-auto w-12 h-12 rounded-full border border-primary/50"
            animate={{
              scale: [1, 2.2],
              opacity: [0.5, 0],
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              repeatDelay: 2.4,
              ease: "easeOut",
            }}
          />
          <BotSweepLogo size={48} />
          {/* Rotating radar sweep arm overlay */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <div
              className="absolute w-px h-[38%] bg-primary/60 origin-bottom"
              style={{ bottom: "50%", left: "50%", transform: "translateX(-50%)" }}
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Labels */}
      <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-10">
        <span className="px-2 py-0.5 rounded text-[9px] font-data text-destructive border border-destructive/30 bg-destructive/10">
          BLOCKED
        </span>
        <span className="px-2 py-0.5 rounded text-[9px] font-data text-green border border-green/30 bg-green/10">
          PASSED
        </span>
      </div>

      {/* Incoming stream line */}
      <div className="absolute top-1/2 left-0 w-[45%] h-px bg-muted-foreground/20" />

      {/* Blocked stream */}
      <div className="absolute left-[55%] w-[45%] h-px bg-destructive/20" style={{ top: "30%" }} />

      {/* Passed stream */}
      <div className="absolute left-[55%] w-[45%] h-px bg-green/20" style={{ top: "70%" }} />

      {/* Particles */}
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className={`absolute w-2 h-2 rounded-full ${p.isBot ? "bg-destructive shadow-[0_0_6px_hsl(var(--destructive)/0.5)]" : "bg-green shadow-[0_0_6px_hsl(var(--green)/0.5)]"}`}
          initial={{ left: "0%", top: "50%", opacity: 0 }}
          animate={{
            left: ["0%", "45%", "95%"],
            top: ["50%", "50%", p.isBot ? "30%" : "70%"],
            opacity: [0, 1, 0],
          }}
          transition={{ duration: 2.5, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
};

export default ParticleFlow;
